/**
 * BettingHUD3D - 3D betting HUD layer using Three.js sprites
 *
 * Replaces DOM betting UI with 3D scene elements:
 * - Banker prompt near drop zone
 * - Locked-in button at bottom
 * - Sidebet panel on right
 * - Denomination/total/balance displays
 *
 * Uses canvas textures consistent with BetZoneManager label style.
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { EventEmitter } from '../../engine/core/EventEmitter';
import { CashAnims } from '../config/AnimationConfig';
import { formatDenomination } from './BillDenominations';
import { sharedRaycaster, sharedPointer } from '../../engine/core/SharedResources';
import { CameraPoseGate } from '../../engine/utils/CameraPoseGate';
import type { AssetLoader } from '../../engine/loaders/AssetLoader';

export interface BettingHUD3DConfig {
  scene: THREE.Scene;
  camera: THREE.Camera;
  assetLoader?: AssetLoader;
  /** Position for banker prompt (near drop zone) */
  bankerPromptPosition?: { x: number; y: number; z: number };
  /** Position for lock-in button (bottom of view) */
  lockInPosition?: { x: number; y: number; z: number };
  /** Position for sidebet panel (right side) */
  sidebetPanelPosition?: { x: number; y: number; z: number };
}

const DEFAULT_CONFIG: Required<Omit<BettingHUD3DConfig, 'scene' | 'camera' | 'assetLoader'>> = {
  bankerPromptPosition: { x: -1.2, y: 1.6, z: -0.4 },
  lockInPosition: { x: -1.5, y: 0.8, z: 5.2 },
  sidebetPanelPosition: { x: 3.9, y: 1.4, z: 0.8 },
};

// Colors matching design system
const GOLD = '#FFD700';
const GREEN = '#00FF88';
const CYAN = '#00F5FF';
const BG_DARK = 'rgba(10, 12, 14, 0.85)';
const BORDER_LIGHT = 'rgba(255, 255, 255, 0.15)';

export interface BettingHUD3DEvents {
  'locked_in': undefined;
  'unlocked': undefined;
  'sidebet_selected': { label: string };
}

interface SidebetListing {
  id: string;
  playerName: string;
  typeLabel: string;
  amount: number;
  targetName?: string;
}

export class BettingHUD3D extends EventEmitter<BettingHUD3DEvents> {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private config: Required<Omit<BettingHUD3DConfig, 'scene' | 'camera' | 'assetLoader'>>;

  // HUD elements
  private hudGroup: THREE.Group;
  private bankerPromptSprite: THREE.Sprite | null = null;
  private lockInModel: THREE.Group | null = null;
  /** When true, the lock-in button is force-hidden through every visibility
   *  path (load, show, setLockInVisible). Used by the tutorial. Default off. */
  private lockInDisabled = false;
  private lockInMixer: THREE.AnimationMixer | null = null;
  private lockInAction: THREE.AnimationAction | null = null;  // single "press" clip, reversed for release
  private lockInGlowLight: THREE.PointLight | null = null;
  /** One-shot scheduled glow-off at the release animation's midpoint —
   *  edge-driven replacement for the old per-frame playback-clock poll. */
  private lockInGlowOffTimer: ReturnType<typeof setTimeout> | null = null;
  private lockInSprite: THREE.Sprite | null = null; // fallback if GLB fails
  private assetLoader: AssetLoader | null = null;
  private totalSprite: THREE.Sprite | null = null;
  private balanceSprite: THREE.Sprite | null = null;
  private sidebetPanelSprite: THREE.Sprite | null = null;
  private sidebetOptionsGroup: THREE.Group;
  private sidebetOptionMeshes: THREE.Mesh[] = [];
  private sidebetButtonGeometry: THREE.PlaneGeometry | null = null;

  // State
  private isVisible = false;
  private isLockedIn = false;
  private lockInAnimating = false;

  // Lock-in button entrance/exit animation (GSAP)
  private lockInEntryTween: gsap.core.Timeline | null = null;
  private currentTotal = 0;
  private currentBalance = 10000;
  /** Count-up tween for balance gains (wins / re-buys). */
  private balanceTween: gsap.core.Tween | null = null;
  private balanceGaining = false;
  private sidebetListings: SidebetListing[] = [];
  private sidebetOptions: string[] = [];
  private showSidebetOptionsPanel = false;
  /** Re-orient the sidebet option meshes only while the camera moves. */
  private billboardGate = new CameraPoseGate();
  private createZoneWorldPos: { x: number; y: number; z: number } | null = null;

  /**
   * When true, sidebet buttons appear as flat 2D UI (parallel to screen).
   * When false, buttons billboard towards camera (3D perspective).
   * Default: true (flat 2D mode)
   */
  private sidebetButtonsFlatMode = true;
  private bankerPromptText = '';
  private showBankerPrompt = false;

  // Canvas textures (reused for updates)
  private bankerPromptCanvas: HTMLCanvasElement | null = null;
  private lockInCanvas: HTMLCanvasElement | null = null;
  private totalCanvas: HTMLCanvasElement | null = null;
  private balanceCanvas: HTMLCanvasElement | null = null;
  private sidebetCanvas: HTMLCanvasElement | null = null;
  private sidebetOptionCanvases: HTMLCanvasElement[] = [];

  // Input handling
  private inputElement: HTMLElement | null = null;
  private boundOnPointerDown: ((e: PointerEvent) => void) | null = null;

  constructor(config: BettingHUD3DConfig) {
    super();
    this.scene = config.scene;
    this.camera = config.camera;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.assetLoader = config.assetLoader ?? null;

    this.hudGroup = new THREE.Group();
    this.hudGroup.name = 'BettingHUD3D';
    this.scene.add(this.hudGroup);

    this.sidebetOptionsGroup = new THREE.Group();
    this.sidebetOptionsGroup.name = 'BettingHUD3D.SidebetOptions';
    this.hudGroup.add(this.sidebetOptionsGroup);

    this.createHUDElements();
    this.hide(); // Start hidden

    // Load 3D lock-in button (replaces canvas sprite when ready)
    this.loadLockInModel();
  }

  private async loadLockInModel(): Promise<void> {
    if (!this.assetLoader) return;
    try {
      const gltf = await this.assetLoader.loadGLTF('models/push_button.glb');
      const model = gltf.scene;

      // Scale only — position goes on wrapper group so rotation pivots around button center
      const BUTTON_SCALE = 1.8;
      model.scale.set(BUTTON_SCALE, BUTTON_SCALE, BUTTON_SCALE);

      // Flat on table surface, dome facing up
      model.rotation.x = 0;

      // Disable shadows on button meshes
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = false;
          child.receiveShadow = false;
        }
      });

      // Red glow light — created once, toggled via visible (no shader recompile)
      this.lockInGlowLight = new THREE.PointLight(0xdd0020, 0, 1.5);
      this.lockInGlowLight.position.set(0, 0.18, 0);
      this.lockInGlowLight.visible = false;
      model.add(this.lockInGlowLight);

      // Set up AnimationMixer — single action approach.
      // Clip: frame 0 (up) → frame 5 (overshoot) → frame 10 (pressed/seated).
      // Play full clip forward to lock in, reverse to unlock.
      this.lockInMixer = new THREE.AnimationMixer(model);
      this.lockInMixer.addEventListener('finished', () => { this.lockInAnimating = false; });
      const pressClip = gltf.animations.find(c => c.name === 'press');
      if (pressClip) {
        const action = this.lockInMixer.clipAction(pressClip);
        action.loop = THREE.LoopOnce;
        action.clampWhenFinished = true;
        this.lockInAction = action;

        // Snap to unpressed pose (frame 0).
        // Play + pause keeps influence active so bind pose doesn't show through.
        action.play();
        action.paused = true;
        action.time = 0;
        this.lockInMixer.update(0);
      }

      this.lockInModel = new THREE.Group();
      this.lockInModel.position.set(
        this.config.lockInPosition.x,
        this.config.lockInPosition.y,
        this.config.lockInPosition.z
      );
      this.lockInModel.add(model);
      this.hudGroup.add(this.lockInModel);

      // Hide canvas fallback sprite
      if (this.lockInSprite) {
        this.lockInSprite.visible = false;
      }

      // Match current HUD visibility (force-hidden when disabled, e.g. tutorial).
      this.lockInModel.visible = this.isVisible && !this.lockInDisabled;
    } catch (err) {
      console.warn('[BettingHUD3D] Failed to load lock-in button model, using canvas fallback', err);
    }
  }

  /**
   * Reposition entire HUD to align with a new ground center.
   * Computes delta from default betting center and shifts the HUD group.
   */
  /**
   * Set the create zone world position so sidebet option buttons
   * can be placed relative to it instead of at hardcoded positions.
   */
  setCreateZonePosition(pos: { x: number; y: number; z: number }): void {
    this.createZoneWorldPos = pos;
  }

  setGroundCenter(center: { x: number; z: number }): void {
    // Default betting center that HUD positions were designed for
    const defaultCenterX = -1.2;
    const defaultCenterZ = 0.2;
    this.hudGroup.position.x = center.x - defaultCenterX;
    this.hudGroup.position.z = center.z - defaultCenterZ;
  }

  /**
   * Attach input handling to a DOM element (typically the canvas)
   */
  attachInput(element: HTMLElement): void {
    this.detachInput();
    this.inputElement = element;
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    element.addEventListener('pointerdown', this.boundOnPointerDown);
  }

  /**
   * Detach input handling
   */
  detachInput(): void {
    if (this.inputElement && this.boundOnPointerDown) {
      this.inputElement.removeEventListener('pointerdown', this.boundOnPointerDown);
    }
    this.inputElement = null;
    this.boundOnPointerDown = null;
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.isVisible) return;

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    sharedPointer.set(x, y);
    sharedRaycaster.setFromCamera(sharedPointer, this.camera);

    // Check lock-in button (3D model or sprite fallback) — toggle on/off
    const lockInTarget = this.lockInModel ?? this.lockInSprite;
    if (lockInTarget) {
      const intersects = sharedRaycaster.intersectObject(lockInTarget, true);
      if (intersects.length > 0) {
        if (this.lockInAnimating) return;

        if (this.isLockedIn) {
          this.setLockedIn(false);
          this.emit('unlocked', undefined);
        } else {
          this.setLockedIn(true);
          this.emit('locked_in', undefined);
        }
        return;
      }
    }

    // Check sidebet options
    if (this.showSidebetOptionsPanel && this.sidebetOptionMeshes.length > 0) {
      const intersects = sharedRaycaster.intersectObjects(this.sidebetOptionMeshes);
      if (intersects.length > 0) {
        const hit = intersects[0].object as THREE.Mesh;
        const index = this.sidebetOptionMeshes.indexOf(hit);
        if (index >= 0 && this.sidebetOptions[index]) {
          const label = this.sidebetOptions[index];
          this.emit('sidebet_selected', { label });
          return;
        }
      }
    }
  }

  private createHUDElements(): void {
    // Lock-in button (bottom)
    this.lockInCanvas = document.createElement('canvas');
    this.lockInSprite = this.createSprite(this.lockInCanvas, 128, 128);
    this.lockInSprite.position.set(
      this.config.lockInPosition.x,
      this.config.lockInPosition.y,
      this.config.lockInPosition.z
    );
    this.lockInSprite.scale.set(1.4, 1.4, 1);
    this.hudGroup.add(this.lockInSprite);

    // Sidebet panel (right side)
    this.sidebetCanvas = document.createElement('canvas');
    this.sidebetPanelSprite = this.createSprite(this.sidebetCanvas, 280, 320);
    this.sidebetPanelSprite.position.set(
      this.config.sidebetPanelPosition.x,
      this.config.sidebetPanelPosition.y,
      this.config.sidebetPanelPosition.z
    );
    this.sidebetPanelSprite.scale.set(2.2, 2.5, 1);
    this.hudGroup.add(this.sidebetPanelSprite);

    this.updateSidebetOptionsSprites();

    // Initial render
    this.updateAllDisplays();
  }

  private createSprite(canvas: HTMLCanvasElement, width: number, height: number): THREE.Sprite {
    canvas.width = width;
    canvas.height = height;

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    return new THREE.Sprite(material);
  }

  private updateAllDisplays(): void {
    this.renderLockInButton();
    this.renderSidebetPanel();
    this.renderSidebetOptions();
  }

  private renderBankerPrompt(): void {
    if (!this.bankerPromptCanvas || !this.bankerPromptSprite) return;

    const ctx = this.bankerPromptCanvas.getContext('2d')!;
    const { width, height } = this.bankerPromptCanvas;

    ctx.clearRect(0, 0, width, height);

    if (!this.showBankerPrompt || !this.bankerPromptText) {
      this.bankerPromptSprite.visible = false;
      this.updateTexture(this.bankerPromptSprite);
      return;
    }

    this.bankerPromptSprite.visible = true;

    // Background
    ctx.fillStyle = BG_DARK;
    this.roundRect(ctx, 0, 0, width, height, 12);
    ctx.fill();

    // Border
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 2;
    this.roundRect(ctx, 1, 1, width - 2, height - 2, 12);
    ctx.stroke();

    // Text
    ctx.font = 'bold 22px "Space Grotesk", sans-serif';
    ctx.fillStyle = GOLD;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.bankerPromptText, width / 2, height / 2);

    this.updateTexture(this.bankerPromptSprite);
  }

  private renderLockInButton(): void {
    if (!this.lockInCanvas || !this.lockInSprite) return;

    const ctx = this.lockInCanvas.getContext('2d')!;
    const { width, height } = this.lockInCanvas;
    const cx = width / 2;
    const cy = height / 2;
    const radius = width * 0.42;

    ctx.clearRect(0, 0, width, height);

    // Outer ring (metallic rim)
    const rimGrad = ctx.createRadialGradient(cx, cy * 0.9, radius * 0.75, cx, cy, radius + 4);
    rimGrad.addColorStop(0, '#555');
    rimGrad.addColorStop(0.5, '#888');
    rimGrad.addColorStop(1, '#333');
    ctx.fillStyle = rimGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius + 4, 0, Math.PI * 2);
    ctx.fill();

    // Main button dome
    const domeGrad = ctx.createRadialGradient(cx, cy * 0.8, radius * 0.1, cx, cy, radius);
    if (this.isLockedIn) {
      domeGrad.addColorStop(0, '#66ffaa');
      domeGrad.addColorStop(0.6, '#00cc66');
      domeGrad.addColorStop(1, '#006633');
    } else {
      domeGrad.addColorStop(0, '#ff6666');
      domeGrad.addColorStop(0.6, '#cc0000');
      domeGrad.addColorStop(1, '#660000');
    }
    ctx.fillStyle = domeGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    // Highlight arc (top-left sheen)
    ctx.save();
    ctx.globalAlpha = 0.3;
    const sheenGrad = ctx.createRadialGradient(cx - radius * 0.3, cy - radius * 0.3, 0, cx, cy, radius);
    sheenGrad.addColorStop(0, '#fff');
    sheenGrad.addColorStop(0.5, 'transparent');
    ctx.fillStyle = sheenGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Text
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 2;
    ctx.font = 'bold 22px "Bebas Neue", "Space Grotesk", sans-serif';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.isLockedIn ? 'LOCKED' : 'LOCK IN', cx, cy);
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    this.updateTexture(this.lockInSprite);
  }

  private renderTotal(): void {
    if (!this.totalCanvas || !this.totalSprite) return;

    const ctx = this.totalCanvas.getContext('2d')!;
    const { width, height } = this.totalCanvas;

    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = BG_DARK;
    this.roundRect(ctx, 0, 0, width, height, 8);
    ctx.fill();

    // Border (green for total)
    ctx.strokeStyle = 'rgba(0, 255, 136, 0.4)';
    ctx.lineWidth = 1;
    this.roundRect(ctx, 1, 1, width - 2, height - 2, 8);
    ctx.stroke();

    // Label
    ctx.font = '11px "Space Grotesk", sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('TOTAL BET', width / 2, 8);

    // Value
    ctx.font = 'bold 24px "Bebas Neue", "Space Grotesk", sans-serif';
    ctx.fillStyle = GREEN;
    ctx.textBaseline = 'middle';
    ctx.fillText(formatDenomination(this.currentTotal), width / 2, height / 2 + 6);

    this.updateTexture(this.totalSprite);
  }

  private renderBalance(): void {
    if (!this.balanceCanvas || !this.balanceSprite) return;

    const ctx = this.balanceCanvas.getContext('2d')!;
    const { width, height } = this.balanceCanvas;

    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = BG_DARK;
    this.roundRect(ctx, 0, 0, width, height, 8);
    ctx.fill();

    // Border
    ctx.strokeStyle = BORDER_LIGHT;
    ctx.lineWidth = 1;
    this.roundRect(ctx, 1, 1, width - 2, height - 2, 8);
    ctx.stroke();

    // Label
    ctx.font = '11px "Space Grotesk", sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('BALANCE', width / 2, 8);

    // Value — tinted green while a gain counts up (cash-increase flourish).
    ctx.font = 'bold 24px "Bebas Neue", "Space Grotesk", sans-serif';
    ctx.fillStyle = this.balanceGaining ? '#00FF88' : '#FFFFFF';
    ctx.textBaseline = 'middle';
    ctx.fillText(formatDenomination(this.currentBalance), width / 2, height / 2 + 6);

    this.updateTexture(this.balanceSprite);
  }

  private renderSidebetPanel(): void {
    if (!this.sidebetCanvas || !this.sidebetPanelSprite) return;

    const ctx = this.sidebetCanvas.getContext('2d')!;
    const { width, height } = this.sidebetCanvas;

    ctx.clearRect(0, 0, width, height);

    if (this.sidebetListings.length === 0) {
      this.sidebetPanelSprite.visible = false;
      this.updateTexture(this.sidebetPanelSprite);
      return;
    }

    this.sidebetPanelSprite.visible = true;

    // Background
    ctx.fillStyle = BG_DARK;
    this.roundRect(ctx, 0, 0, width, height, 12);
    ctx.fill();

    // Border
    ctx.strokeStyle = 'rgba(0, 245, 255, 0.3)';
    ctx.lineWidth = 2;
    this.roundRect(ctx, 1, 1, width - 2, height - 2, 12);
    ctx.stroke();

    // Header
    ctx.font = 'bold 16px "Space Grotesk", sans-serif';
    ctx.fillStyle = CYAN;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('SIDE BETS', width / 2, 12);

    // Divider
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(16, 38);
    ctx.lineTo(width - 16, 38);
    ctx.stroke();

    // Listings
    const startY = 50;
    const itemHeight = 50;
    const maxItems = Math.min(this.sidebetListings.length, 5);

    for (let i = 0; i < maxItems; i++) {
      const listing = this.sidebetListings[i];
      const y = startY + i * itemHeight;

      // Item background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
      this.roundRect(ctx, 8, y, width - 16, itemHeight - 6, 6);
      ctx.fill();

      // Player name
      ctx.font = 'bold 13px "Space Grotesk", sans-serif';
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(listing.playerName, 16, y + 6);

      // Bet type
      ctx.font = '11px "Space Grotesk", sans-serif';
      ctx.fillStyle = CYAN;
      ctx.fillText(listing.typeLabel, 16, y + 24);

      // Amount
      ctx.font = 'bold 14px "Space Grotesk", sans-serif';
      ctx.fillStyle = GREEN;
      ctx.textAlign = 'right';
      ctx.fillText(formatDenomination(listing.amount), width - 16, y + 14);
    }

    // Show more indicator if needed
    if (this.sidebetListings.length > maxItems) {
      ctx.font = '11px "Space Grotesk", sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.textAlign = 'center';
      ctx.fillText(`+${this.sidebetListings.length - maxItems} more`, width / 2, height - 16);
    }

    this.updateTexture(this.sidebetPanelSprite);
  }

  private renderSidebetOptions(): void {
    if (this.sidebetOptions.length === 0) {
      this.sidebetOptionsGroup.visible = false;
      return;
    }

    this.sidebetOptionsGroup.visible = this.showSidebetOptionsPanel;
    for (let i = 0; i < this.sidebetOptionMeshes.length; i++) {
      const mesh = this.sidebetOptionMeshes[i];
      const canvas = this.sidebetOptionCanvases[i];
      const label = this.sidebetOptions[i] ?? '';
      if (!mesh || !canvas) continue;

      const ctx = canvas.getContext('2d')!;
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      // Black background with border radius (fill entire canvas)
      ctx.fillStyle = '#000000';
      this.roundRect(ctx, 0, 0, width, height, 16);
      ctx.fill();

      // White text, larger font, centered (5% bigger = 30px)
      ctx.font = 'bold 30px "Bebas Neue", "Space Grotesk", sans-serif';
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, width / 2, height / 2);

      // Update texture
      const material = mesh.material as THREE.MeshBasicMaterial;
      if (material.map) {
        material.map.needsUpdate = true;
      }
    }
  }

  private updateSidebetOptionsSprites(): void {
    // Dispose old meshes (geometry is shared — disposed once below)
    for (const mesh of this.sidebetOptionMeshes) {
      const material = mesh.material as THREE.MeshBasicMaterial;
      material.map?.dispose();
      material.dispose();
    }
    this.sidebetOptionsGroup.clear();
    this.sidebetOptionMeshes = [];
    this.sidebetOptionCanvases = [];

    if (this.sidebetButtonGeometry) {
      this.sidebetButtonGeometry.dispose();
      this.sidebetButtonGeometry = null;
    }

    if (this.sidebetOptions.length === 0) {
      return;
    }

    // Vertical column layout - 3D planes that face the camera
    // Position relative to create zone if available, else legacy fallback
    const czp = this.createZoneWorldPos;
    // Account for hudGroup offset so buttons land at world position
    const hgx = this.hudGroup.position.x;
    const hgz = this.hudGroup.position.z;
    const baseX = czp ? (czp.x + 1.6 - hgx) : 3.5;
    const baseY = 1.5;
    const baseZ = czp ? (czp.z - hgz) : 1;
    const spacingY = 0.8; // Vertical spacing (margin bottom)
    const buttonWidth = 200;
    const buttonHeight = 56;
    const meshWidth = 1.44; // World units (20% smaller than 1.8)
    const meshHeight = 0.4; // World units

    // Shared geometry for all buttons
    this.sidebetButtonGeometry = new THREE.PlaneGeometry(meshWidth, meshHeight);

    for (let i = 0; i < this.sidebetOptions.length; i++) {
      const canvas = document.createElement('canvas');
      canvas.width = buttonWidth;
      canvas.height = buttonHeight;

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;

      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      });

      // Actually share the geometry — the planes carry no per-instance vertex
      // data, so cloning just duplicated identical buffers per button.
      const mesh = new THREE.Mesh(this.sidebetButtonGeometry, material);
      const y = baseY - i * spacingY; // Vertical layout (stacked down)
      mesh.position.set(baseX, y, baseZ);
      mesh.renderOrder = 100 + i; // Ensure proper render order

      this.sidebetOptionsGroup.add(mesh);
      this.sidebetOptionMeshes.push(mesh);
      this.sidebetOptionCanvases.push(canvas);
    }
  }

  private updateTexture(sprite: THREE.Sprite): void {
    const material = sprite.material as THREE.SpriteMaterial;
    if (material.map) {
      material.map.needsUpdate = true;
    }
  }

  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Show the HUD
   */
  show(): void {
    this.isVisible = true;
    this.hudGroup.visible = true;
    this.setLockedIn(false);
    // Snap button to hidden state (no animation), GameFlowController enables for SIDE_BETTING
    if (this.lockInEntryTween) { this.lockInEntryTween.kill(); this.lockInEntryTween = null; }
    const lockTarget = this.lockInModel ?? this.lockInSprite;
    if (lockTarget) {
      lockTarget.visible = false;
      lockTarget.position.set(this.config.lockInPosition.x - 3.5, this.config.lockInPosition.y, this.config.lockInPosition.z);
      lockTarget.rotation.y = 1.4;
    }
    this.updateAllDisplays();
  }

  /** Permanently suppress the lock-in button on this HUD instance (tutorial).
   *  Guards the load, show, and setLockInVisible paths. Game-safe: default off. */
  disableLockIn(): void {
    this.lockInDisabled = true;
    if (this.lockInEntryTween) { this.lockInEntryTween.kill(); this.lockInEntryTween = null; }
    if (this.lockInModel) this.lockInModel.visible = false;
    if (this.lockInSprite) this.lockInSprite.visible = false;
  }

  /**
   * Show or hide the lock-in button with GSAP slide-in/out animation.
   * Entry: slides from left, tilted on side like a coin, settles flat.
   * Exit: reverse.
   */
  setLockInVisible(visible: boolean): void {
    const target = this.lockInModel ?? this.lockInSprite;
    if (!target) return;
    // Disabled (tutorial): keep it hidden, ignore enable requests.
    if (this.lockInDisabled) { target.visible = false; return; }

    // Kill any running tween
    if (this.lockInEntryTween) {
      this.lockInEntryTween.kill();
      this.lockInEntryTween = null;
    }

    const homeX = this.config.lockInPosition.x;
    const hiddenX = homeX + CashAnims.lockInOffscreenOffset;

    if (visible) {
      target.visible = true;
      this.lockInEntryTween = gsap.timeline()
        .fromTo(target.position, { x: hiddenX }, { x: homeX, duration: CashAnims.lockInEnterDuration, ease: CashAnims.lockInEnterEase }, 0)
        .fromTo(target.rotation, { y: CashAnims.lockInEnterTilt }, { y: 0, duration: CashAnims.lockInRotationDuration, ease: CashAnims.lockInRotationEase }, 0);
    } else {
      // If already hidden, snap
      if (!target.visible) return;
      this.lockInEntryTween = gsap.timeline({
        onComplete: () => { target.visible = false; }
      })
        .to(target.position, { x: hiddenX, duration: CashAnims.lockInExitDuration, ease: CashAnims.lockInExitEase }, 0)
        .to(target.rotation, { y: CashAnims.lockInEnterTilt, duration: CashAnims.lockInExitDuration, ease: CashAnims.lockInExitEase }, 0);
    }
  }

  /**
   * Hide the HUD
   */
  hide(): void {
    this.isVisible = false;
    this.hudGroup.visible = false;
  }

  /**
   * Reset HUD state for phase transition (e.g., betting → shooting).
   * Clears transient state while preserving cached resources.
   * Call this when transitioning away from betting phase.
   */
  reset(): void {
    // Reset locked-in state — snap to unpressed pose (frame 0)
    this.isLockedIn = false;
    this.lockInAnimating = false;
    if (this.lockInEntryTween) { this.lockInEntryTween.kill(); this.lockInEntryTween = null; }
    if (this.lockInAction && this.lockInMixer) {
      this.lockInAction.timeScale = 1;
      this.lockInAction.time = 0;
      this.lockInAction.paused = true;
      this.lockInAction.play();
      this.lockInMixer.update(0);
    }
    if (this.lockInGlowLight) {
      this.lockInGlowLight.visible = false;
      this.lockInGlowLight.intensity = 0;
    }

    // Clear sidebet listings and options
    this.sidebetListings = [];
    this.sidebetOptions = [];
    this.showSidebetOptionsPanel = false;

    // Hide banker prompt
    this.showBankerPrompt = false;
    this.bankerPromptText = '';

    // Reset displays to defaults
    this.currentTotal = 0;

    // Update visuals (will be hidden, but ensures clean state for next show)
    this.renderLockInButton();
    this.renderSidebetPanel();
    this.renderSidebetOptions();
    this.renderBankerPrompt();
    this.renderTotal();

    // Hide the HUD
    this.hide();
  }

  /**
   * Check if visible
   */
  getVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Set locked-in state
   */
  setLockedIn(locked: boolean): void {
    if (this.isLockedIn === locked) return;
    this.isLockedIn = locked;

    // Snap entry tween to end so it doesn't fight the press animation
    if (this.lockInEntryTween) {
      this.lockInEntryTween.progress(1);
      this.lockInEntryTween.kill();
      this.lockInEntryTween = null;
    }

    // Single-action approach: forward = press, reverse = release.
    if (this.lockInAction) {
      this.lockInAnimating = true;
      const clip = this.lockInAction.getClip();
      this.lockInAction.reset();
      this.lockInAction.clampWhenFinished = true;
      this.lockInAction.loop = THREE.LoopOnce;
      if (locked) {
        // Play forward: time 0 (unpressed) → duration (pressed)
        this.lockInAction.timeScale = 1;
        this.lockInAction.time = 0;
      } else {
        // Play reverse: time duration (pressed) → 0 (unpressed)
        this.lockInAction.timeScale = -1;
        this.lockInAction.time = clip.duration;
      }
      this.lockInAction.play();
    }
    // Light on immediately when locking in; off fires as a scheduled one-shot
    // at the release animation's midpoint (edge-driven, not polled per frame).
    // A re-lock before the timer fires cancels it so the fresh glow survives.
    if (this.lockInGlowOffTimer) {
      clearTimeout(this.lockInGlowOffTimer);
      this.lockInGlowOffTimer = null;
    }
    if (this.lockInGlowLight) {
      if (locked) {
        this.lockInGlowLight.visible = true;
        this.lockInGlowLight.intensity = 2;
      } else if (this.lockInGlowLight.visible) {
        const halfMs = this.lockInAction
          ? (this.lockInAction.getClip().duration / 2) * 1000
          : 0;
        this.lockInGlowOffTimer = setTimeout(() => {
          this.lockInGlowOffTimer = null;
          if (!this.lockInGlowLight) return;
          this.lockInGlowLight.visible = false;
          this.lockInGlowLight.intensity = 0;
        }, halfMs);
      }
    }

    // Only re-render canvas fallback if 3D model isn't loaded
    if (!this.lockInModel) {
      this.renderLockInButton();
    }
  }

  /**
   * Get locked-in state
   */
  getLockedIn(): boolean {
    return this.isLockedIn;
  }

  /**
   * Update denomination display
   */
  updateDenomination(_value: number): void {
    // no-op (denomination sprite removed)
  }

  /**
   * Update total bet display
   */
  updateTotal(amount: number): void {
    if (this.currentTotal === amount) return;
    this.currentTotal = amount;
    this.renderTotal();
  }

  /**
   * Update balance display
   */
  updateBalance(amount: number): void {
    if (this.currentBalance === amount) return;

    // Gains (a win landing, or a re-buy topping up the stack) count up with a
    // green cash-increase flourish; spends/losses snap down immediately.
    if (amount > this.currentBalance) {
      this.balanceTween?.kill();
      this.balanceGaining = true;
      const proxy = { v: this.currentBalance };
      this.balanceTween = gsap.to(proxy, {
        v: amount,
        duration: 0.7,
        ease: 'power2.out',
        onUpdate: () => {
          this.currentBalance = Math.round(proxy.v);
          this.renderBalance();
        },
        onComplete: () => {
          this.currentBalance = amount;
          this.balanceGaining = false;
          this.renderBalance();
          this.balanceTween = null;
        },
      });
    } else {
      this.balanceTween?.kill();
      this.balanceTween = null;
      this.balanceGaining = false;
      this.currentBalance = amount;
      this.renderBalance();
    }
  }

  /**
   * Show banker prompt with custom text
   */
  showBankerPromptText(text: string): void {
    this.bankerPromptText = text;
    this.showBankerPrompt = true;
    this.renderBankerPrompt();
  }

  /**
   * Hide banker prompt
   */
  hideBankerPrompt(): void {
    this.showBankerPrompt = false;
    this.renderBankerPrompt();
  }

  /**
   * Set sidebet listings
   */
  setSidebetListings(listings: SidebetListing[]): void {
    this.sidebetListings = listings;
    this.renderSidebetPanel();
  }

  setSidebetOptions(options: string[]): void {
    this.sidebetOptions = options;
    this.updateSidebetOptionsSprites();
    this.renderSidebetOptions();
  }

  setSidebetOptionsVisible(show: boolean): void {
    this.showSidebetOptionsPanel = show;
    this.billboardGate.force(); // orient on the first visible frame
    this.renderSidebetOptions();
  }

  /**
   * Set lock-in button position
   */
  setLockInPosition(position: { x: number; y: number; z: number }): void {
    if (this.lockInSprite) {
      this.lockInSprite.position.set(position.x, position.y, position.z);
    }
  }

  /**
   * Set banker prompt position
   */
  setBankerPromptPosition(position: { x: number; y: number; z: number }): void {
    if (this.bankerPromptSprite) {
      this.bankerPromptSprite.position.set(position.x, position.y, position.z);
    }
  }

  /**
   * Set sidebet panel position
   */
  setSidebetPanelPosition(position: { x: number; y: number; z: number }): void {
    if (this.sidebetPanelSprite) {
      this.sidebetPanelSprite.position.set(position.x, position.y, position.z);
    }
  }

  /**
   * Update (call each frame if needed for animations)
   */
  update(deltaTime: number): void {
    // Tick lock-in button animation
    this.lockInMixer?.update(deltaTime);

    // (Glow-off at the release midpoint is a scheduled one-shot armed in
    // setLockedIn — no per-frame playback-clock polling here.)

    // Lock-in entry/exit handled by GSAP (no manual lerp needed)

    // Orient sidebet option meshes based on mode — only while the camera is
    // actually moving (the panel's whole lifetime is usually a parked camera,
    // so this is a one-time orient on show, then a no-op).
    if (this.showSidebetOptionsPanel && this.sidebetOptionMeshes.length > 0
        && this.billboardGate.changed(this.camera)) {
      for (const mesh of this.sidebetOptionMeshes) {
        if (this.sidebetButtonsFlatMode) {
          // Flat 2D mode: copy camera quaternion so buttons are parallel to screen
          // This makes them appear as flat 2D UI with no perspective distortion
          mesh.quaternion.copy(this.camera.quaternion);
        } else {
          // Billboard mode: buttons face camera position (3D perspective)
          mesh.lookAt(this.camera.position);
        }
      }
    }
  }

  /**
   * Set sidebet buttons display mode
   * @param flat - true for flat 2D UI (parallel to screen), false for 3D billboard
   */
  setSidebetButtonsFlatMode(flat: boolean): void {
    this.sidebetButtonsFlatMode = flat;
    this.billboardGate.force(); // mode changes the orientation math — re-orient
  }

  /**
   * Get current sidebet buttons display mode
   */
  getSidebetButtonsFlatMode(): boolean {
    return this.sidebetButtonsFlatMode;
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    if (this.lockInGlowOffTimer) {
      clearTimeout(this.lockInGlowOffTimer);
      this.lockInGlowOffTimer = null;
    }
    this.detachInput();

    // Dispose sprites and textures
    const sprites = [
      this.bankerPromptSprite,
      this.lockInSprite,
      this.totalSprite,
      this.balanceSprite,
      this.sidebetPanelSprite,
    ];

    for (const sprite of sprites) {
      if (sprite) {
        const material = sprite.material as THREE.SpriteMaterial;
        material.map?.dispose();
        material.dispose();
        this.hudGroup.remove(sprite);
      }
    }

    this.scene.remove(this.hudGroup);

    // Dispose lock-in 3D model
    if (this.lockInModel) {
      this.lockInModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          const mat = child.material;
          if (Array.isArray(mat)) mat.forEach(m => m.dispose());
          else mat.dispose();
        }
      });
      this.hudGroup.remove(this.lockInModel);
      this.lockInModel = null;
    }
    this.lockInMixer?.stopAllAction();
    this.lockInMixer = null;
    this.lockInAction = null;
    this.lockInGlowLight?.dispose();
    this.lockInGlowLight = null;

    this.bankerPromptSprite = null;
    this.lockInSprite = null;
    this.totalSprite = null;
    this.balanceSprite = null;
    this.sidebetPanelSprite = null;

    // Dispose sidebet option meshes (geometry is shared — disposed once below)
    for (const mesh of this.sidebetOptionMeshes) {
      const material = mesh.material as THREE.MeshBasicMaterial;
      material.map?.dispose();
      material.dispose();
    }
    this.sidebetOptionsGroup.clear();
    this.sidebetOptionMeshes = [];

    if (this.sidebetButtonGeometry) {
      this.sidebetButtonGeometry.dispose();
      this.sidebetButtonGeometry = null;
    }

    this.bankerPromptCanvas = null;
    this.lockInCanvas = null;
    this.totalCanvas = null;
    this.balanceCanvas = null;
    this.sidebetCanvas = null;
    this.sidebetOptionCanvases = [];

    this.removeAllListeners();
  }
}
